import { useState, useEffect } from 'react';
import { 
  User, Phone, Mail, MapPin, ArrowRight, ArrowLeft, Upload, Camera, Shield, 
  Info, Calendar, Users, CreditCard, Clock, CheckCircle2, Bike, Footprints,
  Car, AlertCircle, Loader2
} from 'lucide-react';
import { authService } from '../../services/firebase-auth.service';
import { dbService } from '../../services/firebase-db.service';
import { storageService } from '../../services/firebase-storage.service';
import { auth } from '../../config/firebase.config';

interface SignUpScreenProps {
  onSuccess: () => void;
  onBackToLogin: () => void;
  resumeSignup?: boolean;
}

type FleetType = 'motorcycle' | 'bicycle' | 'leg' | null;
type VehicleOwnership = 'own' | 'go7' | null;
type IdDocumentType = 'nin' | 'voters-card' | 'passport';

export function SignUpScreen({ onSuccess, onBackToLogin, resumeSignup = false }: SignUpScreenProps) {
  const [step, setStep] = useState<'phone' | 'otp' | 'personal' | 'fleet' | 'ownership' | 'vehicle' | 'emergency-bank' | 'documents' | 'availability-terms'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  
  // If phone was already verified (returning to finish signup), skip to personal details
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser?.phoneNumber) {
      const phone = currentUser.phoneNumber.replace('+234', '');
      setPhoneNumber(phone);
      if (resumeSignup || sessionStorage.getItem('go7-signup-in-progress') === '1') {
        setStep('personal');
      }
    }
  }, [resumeSignup]);
  
  // Personal details
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [nin, setNin] = useState(''); // National ID Number
  
  // Fleet type
  const [fleetType, setFleetType] = useState<FleetType>(null);
  const [vehicleOwnership, setVehicleOwnership] = useState<VehicleOwnership>(null);
  
  // Vehicle details (conditional)
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [vehicleColor, setVehicleColor] = useState('');
  
  // Emergency contact
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [emergencyRelationship, setEmergencyRelationship] = useState('');
  
  // Bank details
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  
  // Referral
  const [referralCode, setReferralCode] = useState('');
  
  // Documents
  const [idDocumentType, setIdDocumentType] = useState<IdDocumentType | null>(null);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [idCard, setIdCard] = useState<string | null>(null);
  const [selfieWithId, setSelfieWithId] = useState<string | null>(null);
  const [vehicleReg, setVehicleReg] = useState<string | null>(null);
  
  // Work availability
  const [flexibleSchedule, setFlexibleSchedule] = useState(false);
  const [availableDays, setAvailableDays] = useState<string[]>([]);
  const [preferredHours, setPreferredHours] = useState('');
  
  // Agreements — single combined consent
  const [agreementsAccepted, setAgreementsAccepted] = useState(false);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate phone number
    if (phoneNumber.length !== 10 || !/^\d{10}$/.test(phoneNumber)) {
      setError('Phone number must be exactly 10 digits');
      return;
    }
    
    setLoading(true);

    try {
      const fullPhone = `+234${phoneNumber}`;
      await authService.sendOTP(fullPhone);
      setStep('otp');
    } catch (err: any) {
      console.error('Failed to send OTP:', err);
      setError(err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = async (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }

      if (index === 5 && value && newOtp.every(digit => digit)) {
        setLoading(true);
        setError('');
        
        try {
          const otpCode = newOtp.join('');
          const fullPhone = `+234${phoneNumber}`;
          await authService.verifyOTP(fullPhone, otpCode);
          sessionStorage.setItem('go7-signup-in-progress', '1');
          console.log('Phone verified successfully!');
          setTimeout(() => setStep('personal'), 500);
        } catch (err: any) {
          console.error('OTP verification failed:', err);
          setError('Invalid code. Please try again.');
          setOtp(['', '', '', '', '', '']);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePersonalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (fullName && email && address && dateOfBirth && nin) {
      // Validate NIN (11 digits)
      if (nin.length !== 11 || !/^\d{11}$/.test(nin)) {
        alert('NIN must be exactly 11 digits');
        return;
      }
      
      // Check age (must be 18+)
      const birthDate = new Date(dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (age < 18) {
        alert('You must be at least 18 years old to sign up as a rider');
        return;
      }
      setStep('fleet');
    }
  };

  const handleFleetSelection = (type: FleetType) => {
    setFleetType(type);
    // Leg fleet = walking, no vehicle needed
    // Motorcycle & Bicycle = ask if using own or GO7-provided
    if (type === 'leg') {
      setVehicleOwnership(null); // No vehicle
      setStep('emergency-bank');
    } else {
      // Ask about vehicle ownership (own vs GO7-provided)
      setStep('ownership');
    }
  };

  const handleOwnershipSelection = (ownership: VehicleOwnership) => {
    setVehicleOwnership(ownership);
    if (ownership === 'own') {
      // Using own vehicle - need details
      setStep('vehicle');
    } else {
      // Using GO7 vehicle - skip details
      setStep('emergency-bank');
    }
  };

  const handleVehicleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (fleetType === 'motorcycle') {
      if (vehicleMake && vehicleModel && licensePlate && vehicleColor) {
        setStep('emergency-bank');
      }
    } else if (fleetType === 'bicycle') {
      if (vehicleMake && vehicleModel && vehicleColor) {
        setStep('emergency-bank');
      }
    }
  };

  const handleEmergencyBankSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate emergency phone
    if (emergencyPhone.length !== 10 || !/^\d{10}$/.test(emergencyPhone)) {
      alert('Emergency phone number must be exactly 10 digits');
      return;
    }
    
    // Validate account number
    if (accountNumber.length !== 10 || !/^\d{10}$/.test(accountNumber)) {
      alert('Account number must be exactly 10 digits');
      return;
    }
    
    if (emergencyName && emergencyPhone && emergencyRelationship && bankName && accountNumber && accountName) {
      setStep('documents');
    }
  };

  const handleFileUpload = (type: 'profile' | 'id' | 'selfie' | 'vehicle', file: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (JPG, PNG, or similar)');
      return;
    }
    // Cap at ~8MB — data URLs balloon in memory
    if (file.size > 8 * 1024 * 1024) {
      setError('Image is too large. Please use a photo under 8MB.');
      return;
    }
    setError('');
    const reader = new FileReader();
    reader.onerror = () => setError('Could not read that photo. Try another image.');
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (type === 'profile') setProfilePhoto(result);
      else if (type === 'id') setIdCard(result);
      else if (type === 'selfie') setSelfieWithId(result);
      else if (type === 'vehicle') setVehicleReg(result);
    };
    reader.readAsDataURL(file);
  };

  const getIdDocumentLabel = (type: IdDocumentType) => {
    switch (type) {
      case 'nin': return 'NIN Slip or National ID Card';
      case 'voters-card': return "Voter's Card";
      case 'passport': return 'International Passport';
    }
  };

  const documentsReady = Boolean(idDocumentType && profilePhoto && idCard);

  const handleDocumentsSubmit = () => {
    if (!idDocumentType) {
      setError('Please choose your ID type (NIN, Voter\'s Card, or Passport)');
      return;
    }
    if (!profilePhoto) {
      setError('Please upload a profile photo');
      return;
    }
    if (!idCard) {
      setError(`Please upload your ${getIdDocumentLabel(idDocumentType)}`);
      return;
    }
    setError('');
    setStep('availability-terms');
  };

  const toggleDay = (day: string) => {
    if (availableDays.includes(day)) {
      setAvailableDays(availableDays.filter(d => d !== day));
    } else {
      setAvailableDays([...availableDays, day]);
    }
  };

  const handleFinalSubmit = async () => {
    console.log('🚀 Starting final submission...');
    console.log('Validation checks:', {
      agreementsAccepted,
      flexibleSchedule,
      availableDaysCount: availableDays.length,
      preferredHours
    });
    
    if (!agreementsAccepted) {
      alert('Please accept the terms and agreements to continue');
      return;
    }

    if (!flexibleSchedule && availableDays.length === 0) {
      alert('Select your available days or choose a flexible schedule');
      return;
    }

    if (!preferredHours) {
      alert('Please select your preferred working hours');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Get current authenticated user
      const currentUser = authService.getCurrentUser();
      
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      
      console.log('Creating rider profile with all documents...');
      
      // Upload all documents to Firebase Storage
      let profilePhotoURL = '';
      let govIdURL = '';
      let selfieIdURL = '';
      let vehicleRegURL = '';
      
      if (profilePhoto && profilePhoto.startsWith('data:')) {
        const blob = await fetch(profilePhoto).then(r => r.blob());
        const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
        profilePhotoURL = await storageService.uploadProfilePhoto(currentUser.uid, file);
      }
      
      if (idCard && idCard.startsWith('data:')) {
        const blob = await fetch(idCard).then(r => r.blob());
        const file = new File([blob], 'government_id.jpg', { type: 'image/jpeg' });
        govIdURL = await storageService.uploadDocument(currentUser.uid, 'government_id', file);
      }
      
      if (selfieWithId && selfieWithId.startsWith('data:')) {
        const blob = await fetch(selfieWithId).then(r => r.blob());
        const file = new File([blob], 'selfie_with_id.jpg', { type: 'image/jpeg' });
        selfieIdURL = await storageService.uploadDocument(currentUser.uid, 'selfie_with_id', file);
      }
      
      if (vehicleReg && vehicleReg.startsWith('data:')) {
        const blob = await fetch(vehicleReg).then(r => r.blob());
        const file = new File([blob], 'vehicle_reg.jpg', { type: 'image/jpeg' });
        vehicleRegURL = await storageService.uploadDocument(currentUser.uid, 'vehicle_reg', file);
      }
      
      console.log('📄 Documents uploaded, creating profile...');
      
      const resolvedFleetType = (fleetType === 'motorcycle' ? 'rider' : fleetType === 'leg' ? 'leg-fleet' : fleetType) as 'rider' | 'bicycle' | 'leg-fleet';
      const resolvedVehicleType: 'bike' | 'bicycle' | 'leg' =
        resolvedFleetType === 'rider' ? 'bike' :
        resolvedFleetType === 'leg-fleet' ? 'leg' : 'bicycle';

      // Create rider profile in Firestore with all data
      const profileData: any = {
        name: fullName,
        email: email,
        phone: `+234${phoneNumber}`,
        address: address,
        dateOfBirth: dateOfBirth,
        nin: nin,
        fleetType: resolvedFleetType,
        vehicleType: resolvedVehicleType,
        idDocumentType: idDocumentType,
        photoURL: profilePhotoURL,
        availability: {
          flexibleSchedule,
          availableDays,
          preferredHours,
        },
        referralCode: referralCode || null,
        emergencyContact: {
          name: emergencyName,
          phone: `+234${emergencyPhone}`,
          relationship: emergencyRelationship,
        },
        bankDetails: {
          bankName: bankName,
          accountNumber: accountNumber,
          accountName: accountName,
        },
      };
      
      // Only add vehicleOwnership if it exists
      if (vehicleOwnership) {
        profileData.vehicleOwnership = vehicleOwnership;
      }
      
      // Only add vehicleInfo if using own vehicle
      if (vehicleOwnership === 'own') {
        profileData.vehicleInfo = {
          make: vehicleMake,
          model: vehicleModel,
          color: vehicleColor,
        };
        if (licensePlate) {
          profileData.vehicleInfo.licensePlate = licensePlate;
        }
      }
      
      // Add documents (only if URLs exist)
      profileData.documents = {};
      if (govIdURL) profileData.documents.governmentId = govIdURL;
      if (selfieIdURL) profileData.documents.selfieWithId = selfieIdURL;
      if (vehicleRegURL) profileData.documents.vehicleRegistration = vehicleRegURL;
      
      await dbService.createRiderProfile(currentUser.uid, profileData);
      
      console.log('✅ Rider profile created successfully! Awaiting admin approval.');
      
      // Show success message
      alert('✅ Registration Complete!\n\nYour application has been submitted successfully. You will be notified once it has been reviewed (typically within 24-48 hours).');
      
      onSuccess();
    } catch (err: any) {
      console.error('❌ Failed to create rider profile:', err);
      setError(err.message || 'Failed to complete signup. Please try again.');
      alert('Signup failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 'phone': return 'Join GO7';
      case 'otp': return 'Verify your number';
      case 'personal': return 'Personal information';
      case 'fleet': return 'Choose your fleet';
      case 'ownership': return 'Vehicle ownership';
      case 'vehicle': return 'Vehicle details';
      case 'emergency-bank': return 'Contact & Payment';
      case 'documents': return 'Upload documents';
      case 'availability-terms': return 'Final steps';
      default: return '';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 'phone': return 'Start earning as a GO7 rider today';
      case 'otp': return `We sent a code to ${phoneNumber}`;
      case 'personal': return 'Tell us about yourself';
      case 'fleet': return 'Select your delivery vehicle type';
      case 'ownership': return `Do you have your own ${fleetType}?`;
      case 'vehicle': return 'Provide your vehicle information';
      case 'emergency-bank': return 'Emergency contact and payment details';
      case 'documents': return 'Choose your ID type and upload only what you need';
      case 'availability-terms': return 'Set your schedule and review terms';
      default: return '';
    }
  };

  const getStepIcon = () => {
    switch (step) {
      case 'phone':
      case 'otp':
        return <Phone className="w-8 h-8 text-[#E0001B]" />;
      case 'personal':
        return <User className="w-8 h-8 text-[#E0001B]" />;
      case 'fleet':
        return <Bike className="w-8 h-8 text-[#E0001B]" />;
      case 'ownership':
        return <Bike className="w-8 h-8 text-[#E0001B]" />;
      case 'vehicle':
        return <Car className="w-8 h-8 text-[#E0001B]" />;
      case 'emergency-bank':
        return <CreditCard className="w-8 h-8 text-[#E0001B]" />;
      case 'documents':
        return <Camera className="w-8 h-8 text-[#E0001B]" />;
      case 'availability-terms':
        return <CheckCircle2 className="w-8 h-8 text-[#E0001B]" />;
      default:
        return <User className="w-8 h-8 text-[#E0001B]" />;
    }
  };

  const handleBack = () => {
    if (step === 'phone') onBackToLogin();
    else if (step === 'otp') setStep('phone');
    else if (step === 'personal') setStep('otp');
    else if (step === 'fleet') setStep('personal');
    else if (step === 'ownership') setStep('fleet');
    else if (step === 'vehicle') setStep('ownership');
    else if (step === 'emergency-bank') {
      // Navigate back based on vehicle ownership choice
      if (fleetType === 'leg') {
        setStep('fleet');
      } else if (vehicleOwnership === 'own') {
        setStep('vehicle');
      } else {
        setStep('ownership');
      }
    }
    else if (step === 'documents') setStep('emergency-bank');
    else if (step === 'availability-terms') setStep('documents');
  };

  const allSteps = ['phone', 'otp', 'personal', 'fleet', 'ownership', 'vehicle', 'emergency-bank', 'documents', 'availability-terms'];
  // Calculate progress: skip ownership for leg, skip vehicle for GO7-provided
  const activeSteps = fleetType === 'leg'
    ? allSteps.filter(s => s !== 'ownership' && s !== 'vehicle')
    : vehicleOwnership === 'go7'
    ? allSteps.filter(s => s !== 'vehicle')
    : allSteps;
  const currentStepIndex = activeSteps.indexOf(step);
  const progressPercentage = ((currentStepIndex + 1) / activeSteps.length) * 100;

  return (
    <div className="absolute inset-0 bg-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-8 pt-12 flex-shrink-0">
        <button
          onClick={handleBack}
          className="mb-6 flex items-center gap-2 text-[#6B6B6B] hover:text-[#111111] transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>

        <div className="w-16 h-16 rounded-full bg-[#E0001B]/10 flex items-center justify-center mb-6">
          {getStepIcon()}
        </div>
        
        <h1 className="text-[28px] text-[#111111] mb-2">
          {getStepTitle()}
        </h1>
        
        <p className="text-[#6B6B6B]">
          {getStepDescription()}
        </p>

        {/* Progress bar */}
        <div className="mt-6">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#E0001B] transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <p className="text-[12px] text-[#6B6B6B] mt-2">
            Step {currentStepIndex + 1} of {activeSteps.length}
          </p>
        </div>
        
        {/* Resume signup notice — only for returning users, not brand-new signups */}
        {resumeSignup && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-[12px]">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">Continue your signup</h3>
                <p className="text-[14px] text-blue-800">
                  Pick up where you left off and complete the steps below to submit your rider application.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Form Content */}
      <div className="flex-1 px-8 overflow-y-auto overscroll-contain pb-10 min-h-0">
        {/* Step 1: Phone Number */}
        {step === 'phone' && (
          <form onSubmit={handlePhoneSubmit} className="space-y-6">
            {error && (
              <div className="flex items-start gap-3 p-4 bg-red-50 rounded-[12px] border border-red-200">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-[14px] text-red-900">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="phone" className="block text-[14px] mb-2">
                Mobile Number
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2 text-[#6B6B6B]">
                  <Phone className="w-4 h-4" />
                  <span>+234</span>
                </div>
                <input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                  placeholder="810 123 4567"
                  className="w-full h-14 pl-24 pr-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                  maxLength={10}
                />
              </div>
            </div>

            {/* reCAPTCHA container */}


            <button
              type="submit"
              disabled={phoneNumber.length < 10 || loading}
              className="w-full h-12 rounded-[12px] bg-[#E0001B] text-white hover:bg-[#C00017] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending OTP...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            <div className="text-center">
              <p className="text-[14px] text-[#6B6B6B]">
                Already have an account?{' '}
                <button 
                  type="button"
                  onClick={onBackToLogin}
                  className="text-[#E0001B] hover:underline"
                >
                  Sign in
                </button>
              </p>
            </div>
          </form>
        )}

        {/* Step 2: OTP Verification */}
        {step === 'otp' && (
          <div className="space-y-6">
            <div>
              <label className="block text-[14px] mb-3">
                Verification Code
              </label>
              <div className="flex gap-2 justify-between">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="w-12 h-14 text-center rounded-[12px] bg-gray-50 border-2 border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                    maxLength={1}
                  />
                ))}
              </div>
            </div>

            <button
              onClick={async () => {
                try {
                  const fullPhone = `+234${phoneNumber}`;
                  await authService.sendOTP(fullPhone);
                  setOtp(['', '', '', '', '', '']);
                  setError('');
                } catch (err: any) {
                  setError(err.message || 'Failed to resend OTP');
                }
              }}
              className="w-full text-[14px] text-[#E0001B] hover:underline"
            >
              Resend code
            </button>
          </div>
        )}

        {/* Step 3: Personal Information */}
        {step === 'personal' && (
          <form onSubmit={handlePersonalSubmit} className="space-y-6">
            <div>
              <label htmlFor="fullName" className="block text-[14px] mb-2">
                Full Name <span className="text-[#E0001B]">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B]" />
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full h-14 pl-12 pr-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="dateOfBirth" className="block text-[14px] mb-2">
                Date of Birth <span className="text-[#E0001B]">*</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B]" />
                <input
                  id="dateOfBirth"
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                  className="w-full h-14 pl-12 pr-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                  required
                />
              </div>
              <p className="text-[12px] text-[#6B6B6B] mt-1">You must be at least 18 years old</p>
            </div>

            <div>
              <label htmlFor="nin" className="block text-[14px] mb-2">
                National ID Number (NIN) <span className="text-[#E0001B]">*</span>
              </label>
              <div className="relative">
                <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B]" />
                <input
                  id="nin"
                  type="text"
                  inputMode="numeric"
                  value={nin}
                  onChange={(e) => setNin(e.target.value.replace(/\D/g, '').slice(0, 11))}
                  placeholder="12345678901"
                  maxLength={11}
                  className="w-full h-14 pl-12 pr-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                  required
                />
              </div>
              <p className="text-[12px] text-[#6B6B6B] mt-1">11-digit National Identity Number for verification</p>
            </div>

            <div>
              <label htmlFor="email" className="block text-[14px] mb-2">
                Email Address <span className="text-[#E0001B]">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B]" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john.doe@example.com"
                  className="w-full h-14 pl-12 pr-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="address" className="block text-[14px] mb-2">
                Home Address <span className="text-[#E0001B]">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B6B]" />
                <input
                  id="address"
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="12 Victoria Island, Lagos"
                  className="w-full h-14 pl-12 pr-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={!fullName || !email || !address || !dateOfBirth || !nin}
              className="w-full h-12 rounded-[12px] bg-[#E0001B] text-white hover:bg-[#C00017] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              Continue
              <ArrowRight className="w-5 h-5" />
            </button>
          </form>
        )}

        {/* Step 4: Fleet Type Selection */}
        {step === 'fleet' && (
          <div className="space-y-6">
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-[12px] border border-blue-100 mb-6">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[14px] text-blue-900 font-medium">Choose Your Fleet Type</p>
                <p className="text-[12px] text-blue-700 mt-0.5">
                  Select the delivery method you'll be using. This affects job assignments and requirements.
                </p>
              </div>
            </div>

            <button
              onClick={() => handleFleetSelection('motorcycle')}
              className="w-full p-6 rounded-[12px] bg-white border-2 border-gray-200 hover:border-[#E0001B] transition-colors text-left"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#E0001B]/10 flex items-center justify-center flex-shrink-0">
                  <Car className="w-6 h-6 text-[#E0001B]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-[16px] font-medium mb-1">Motorcycle / Scooter</h3>
                  <p className="text-[14px] text-[#6B6B6B] mb-2">Fast deliveries for longer distances and heavier packages</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-[12px] rounded">Higher earnings</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-[12px] rounded">Fast & efficient</span>
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 text-[12px] rounded">Registration needed</span>
                  </div>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleFleetSelection('bicycle')}
              className="w-full p-6 rounded-[12px] bg-white border-2 border-gray-200 hover:border-[#E0001B] transition-colors text-left"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#00B277]/10 flex items-center justify-center flex-shrink-0">
                  <Bike className="w-6 h-6 text-[#00B277]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-[16px] font-medium mb-1">Bicycle</h3>
                  <p className="text-[14px] text-[#6B6B6B] mb-2">Eco-friendly deliveries for medium distances</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-[12px] rounded">Eco-friendly</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-[12px] rounded">No license needed</span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-[12px] rounded">Health benefits</span>
                  </div>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleFleetSelection('leg')}
              className="w-full p-6 rounded-[12px] bg-white border-2 border-gray-200 hover:border-[#E0001B] transition-colors text-left"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#FF6B00]/10 flex items-center justify-center flex-shrink-0">
                  <Footprints className="w-6 h-6 text-[#FF6B00]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-[16px] font-medium mb-1">Leg Fleet (Walking)</h3>
                  <p className="text-[14px] text-[#6B6B6B] mb-2">Hyperlocal deliveries under 2km</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-[12px] rounded">Quick start</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-[12px] rounded">No vehicle needed</span>
                  </div>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Step 4.5: Vehicle Ownership Selection */}
        {step === 'ownership' && (
          <div className="space-y-6">
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-[12px] border border-blue-100 mb-6">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[14px] text-blue-900 font-medium">Vehicle Ownership</p>
                <p className="text-[12px] text-blue-700 mt-0.5">
                  Do you have your own {fleetType} or would you like to use a GO7-provided {fleetType}?
                </p>
              </div>
            </div>

            <button
              onClick={() => handleOwnershipSelection('own')}
              className="w-full p-6 rounded-[12px] bg-white border-2 border-gray-200 hover:border-[#E0001B] transition-colors text-left"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#E0001B]/10 flex items-center justify-center flex-shrink-0">
                  {fleetType === 'motorcycle' ? (
                    <Car className="w-6 h-6 text-[#E0001B]" />
                  ) : (
                    <Bike className="w-6 h-6 text-[#E0001B]" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-[16px] font-medium mb-1">Use My Own {fleetType === 'motorcycle' ? 'Motorcycle' : 'Bicycle'}</h3>
                  <p className="text-[14px] text-[#6B6B6B] mb-2">You'll need to provide vehicle details and registration documents</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-[12px] rounded">Keep 100% flexibility</span>
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 text-[12px] rounded">Need vehicle details</span>
                  </div>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleOwnershipSelection('go7')}
              className="w-full p-6 rounded-[12px] bg-white border-2 border-gray-200 hover:border-[#E0001B] transition-colors text-left"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#00B277]/10 flex items-center justify-center flex-shrink-0">
                  {fleetType === 'motorcycle' ? (
                    <Car className="w-6 h-6 text-[#00B277]" />
                  ) : (
                    <Bike className="w-6 h-6 text-[#00B277]" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-[16px] font-medium mb-1">Use GO7 {fleetType === 'motorcycle' ? 'Motorcycle' : 'Bicycle'}</h3>
                  <p className="text-[14px] text-[#6B6B6B] mb-2">GO7 provides the {fleetType}, no vehicle registration needed</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-[12px] rounded">GO7 provides equipment</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-[12px] rounded">Quick start</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-[12px] rounded">No maintenance cost</span>
                  </div>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Step 5: Vehicle Details (Conditional) */}
        {step === 'vehicle' && (
          <form onSubmit={handleVehicleSubmit} className="space-y-6">
            <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-[12px] border border-yellow-100 mb-6">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[14px] text-yellow-900 font-medium">Vehicle Information Required</p>
                <p className="text-[12px] text-yellow-700 mt-0.5">
                  We need details about your {fleetType} for insurance and verification purposes.
                </p>
              </div>
            </div>

            <div>
              <label htmlFor="vehicleMake" className="block text-[14px] mb-2">
                Vehicle Make <span className="text-[#E0001B]">*</span>
              </label>
              <input
                id="vehicleMake"
                type="text"
                value={vehicleMake}
                onChange={(e) => setVehicleMake(e.target.value)}
                placeholder="e.g., Honda, Yamaha, Trek"
                className="w-full h-14 px-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                required
              />
            </div>

            <div>
              <label htmlFor="vehicleModel" className="block text-[14px] mb-2">
                Vehicle Model <span className="text-[#E0001B]">*</span>
              </label>
              <input
                id="vehicleModel"
                type="text"
                value={vehicleModel}
                onChange={(e) => setVehicleModel(e.target.value)}
                placeholder="e.g., Wave 110, FX 3"
                className="w-full h-14 px-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                required
              />
            </div>

            {fleetType === 'motorcycle' && (
              <>
                <div>
                  <label htmlFor="licensePlate" className="block text-[14px] mb-2">
                    License Plate Number <span className="text-[#E0001B]">*</span>
                  </label>
                  <input
                    id="licensePlate"
                    type="text"
                    value={licensePlate}
                    onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
                    placeholder="e.g., ABC-123-XY"
                    className="w-full h-14 px-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                    required
                  />
                </div>

              </>
            )}

            <div>
              <label htmlFor="vehicleColor" className="block text-[14px] mb-2">
                Vehicle Color <span className="text-[#E0001B]">*</span>
              </label>
              <input
                id="vehicleColor"
                type="text"
                value={vehicleColor}
                onChange={(e) => setVehicleColor(e.target.value)}
                placeholder="e.g., Black, Red, Silver"
                className="w-full h-14 px-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full h-12 rounded-[12px] bg-[#E0001B] text-white hover:bg-[#C00017] transition-colors shadow-sm flex items-center justify-center gap-2"
            >
              Continue
              <ArrowRight className="w-5 h-5" />
            </button>
          </form>
        )}

        {/* Step 6: Emergency Contact & Bank Details */}
        {step === 'emergency-bank' && (
          <form onSubmit={handleEmergencyBankSubmit} className="space-y-6">
            {/* Emergency Contact Section */}
            <div>
              <h3 className="text-[18px] font-medium mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#E0001B]" />
                Emergency Contact
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="emergencyName" className="block text-[14px] mb-2">
                    Contact Name <span className="text-[#E0001B]">*</span>
                  </label>
                  <input
                    id="emergencyName"
                    type="text"
                    value={emergencyName}
                    onChange={(e) => setEmergencyName(e.target.value)}
                    placeholder="Jane Doe"
                    className="w-full h-14 px-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="emergencyPhone" className="block text-[14px] mb-2">
                    Contact Phone <span className="text-[#E0001B]">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B6B6B]">+234</span>
                    <input
                      id="emergencyPhone"
                      type="tel"
                      inputMode="numeric"
                      value={emergencyPhone}
                      onChange={(e) => setEmergencyPhone(e.target.value.replace(/\D/g, ''))}
                      placeholder="810 123 4567"
                      className="w-full h-14 pl-20 pr-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                      maxLength={10}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="emergencyRelationship" className="block text-[14px] mb-2">
                    Relationship <span className="text-[#E0001B]">*</span>
                  </label>
                  <select
                    id="emergencyRelationship"
                    value={emergencyRelationship}
                    onChange={(e) => setEmergencyRelationship(e.target.value)}
                    className="w-full h-14 px-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                    required
                  >
                    <option value="">Select relationship</option>
                    <option value="parent">Parent</option>
                    <option value="spouse">Spouse</option>
                    <option value="sibling">Sibling</option>
                    <option value="child">Child</option>
                    <option value="friend">Friend</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Bank Details Section */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-[18px] font-medium mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#E0001B]" />
                Bank Account Details
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="bankName" className="block text-[14px] mb-2">
                    Bank Name <span className="text-[#E0001B]">*</span>
                  </label>
                  <select
                    id="bankName"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    className="w-full h-14 px-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                    required
                  >
                    <option value="">Select your bank</option>
                    <option value="access">Access Bank</option>
                    <option value="gtb">GTBank</option>
                    <option value="firstbank">First Bank</option>
                    <option value="uba">UBA</option>
                    <option value="zenith">Zenith Bank</option>
                    <option value="fidelity">Fidelity Bank</option>
                    <option value="union">Union Bank</option>
                    <option value="sterling">Sterling Bank</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="accountNumber" className="block text-[14px] mb-2">
                    Account Number <span className="text-[#E0001B]">*</span>
                  </label>
                  <input
                    id="accountNumber"
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                    placeholder="0123456789"
                    className="w-full h-14 px-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                    maxLength={10}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="accountName" className="block text-[14px] mb-2">
                    Account Name <span className="text-[#E0001B]">*</span>
                  </label>
                  <input
                    id="accountName"
                    type="text"
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value.toUpperCase())}
                    placeholder="JOHN DOE"
                    className="w-full h-14 px-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                    required
                  />
                  <p className="text-[12px] text-[#6B6B6B] mt-1">Enter name exactly as it appears on your bank account</p>
                </div>
              </div>
            </div>

            {/* Referral Code (Optional) */}
            <div className="pt-6 border-t border-gray-200">
              <div>
                <label htmlFor="referralCode" className="block text-[14px] mb-2">
                  Referral Code <span className="text-[#6B6B6B]">(Optional)</span>
                </label>
                <input
                  id="referralCode"
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder="Enter code if you have one"
                  className="w-full h-14 px-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                />
                <p className="text-[12px] text-[#6B6B6B] mt-1">Get bonuses when referred by existing riders</p>
              </div>
            </div>

            <button
              type="submit"
              className="w-full h-12 rounded-[12px] bg-[#E0001B] text-white hover:bg-[#C00017] transition-colors shadow-sm flex items-center justify-center gap-2"
            >
              Continue
              <ArrowRight className="w-5 h-5" />
            </button>
          </form>
        )}

        {/* Step 7: Documents Upload */}
        {step === 'documents' && (
          <div className="space-y-6 pb-4">
            {/* Info Card */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-[12px] border border-blue-100">
              <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[14px] text-blue-900 font-medium">Choose your verification method</p>
                <p className="text-[12px] text-blue-700 mt-0.5">
                  Pick one ID type below. You only need to upload that document plus a profile photo.
                </p>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-3 p-4 bg-red-50 rounded-[12px] border border-red-200">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-[14px] text-red-900">{error}</p>
              </div>
            )}

            {/* ID type selection */}
            <div>
              <label className="block text-[14px] mb-2">
                Identity document type <span className="text-[#E0001B]">*</span>
              </label>
              <div className="flex flex-col gap-2">
                {([
                  { value: 'nin' as IdDocumentType, label: 'NIN Slip / National ID' },
                  { value: 'voters-card' as IdDocumentType, label: "Voter's Card" },
                  { value: 'passport' as IdDocumentType, label: 'International Passport' },
                ]).map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setIdDocumentType(value);
                      setIdCard(null);
                      setError('');
                    }}
                    className={`min-h-12 w-full px-4 py-3 rounded-[12px] border-2 text-[14px] text-left transition-colors ${
                      idDocumentType === value
                        ? 'bg-[#E0001B] border-[#E0001B] text-white'
                        : 'bg-white border-gray-200 text-[#111111] hover:border-[#E0001B]'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Profile Photo */}
            <div>
              <label className="block text-[14px] mb-2">
                Profile Photo <span className="text-[#E0001B]">*</span>
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  capture="user"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload('profile', file);
                    e.target.value = '';
                  }}
                  className="hidden"
                  id="profile-upload"
                />
                <label
                  htmlFor="profile-upload"
                  className="w-full h-32 rounded-[12px] bg-gray-50 border-2 border-dashed border-gray-300 hover:border-[#E0001B] transition-colors flex flex-col items-center justify-center cursor-pointer overflow-hidden"
                >
                  {profilePhoto ? (
                    <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-[#6B6B6B] mb-2" />
                      <span className="text-[14px] text-[#6B6B6B]">Upload clear headshot</span>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Selected government ID */}
            {idDocumentType ? (
              <div>
                <label className="block text-[14px] mb-2">
                  {getIdDocumentLabel(idDocumentType)} <span className="text-[#E0001B]">*</span>
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload('id', file);
                      e.target.value = '';
                    }}
                    className="hidden"
                    id="id-upload"
                  />
                  <label
                    htmlFor="id-upload"
                    className="w-full h-32 rounded-[12px] bg-gray-50 border-2 border-dashed border-gray-300 hover:border-[#E0001B] transition-colors flex flex-col items-center justify-center cursor-pointer overflow-hidden"
                  >
                    {idCard ? (
                      <img src={idCard} alt="ID Card" className="w-full h-full object-cover" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-[#6B6B6B] mb-2" />
                        <span className="text-[14px] text-[#6B6B6B] text-center px-4">
                          Upload clear photo of your {getIdDocumentLabel(idDocumentType)}
                        </span>
                      </>
                    )}
                  </label>
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-[12px] bg-amber-50 border border-amber-200 text-[13px] text-amber-900">
                Select an ID type above to unlock the ID upload field.
              </div>
            )}

            {/* Selfie with ID — optional */}
            <div>
              <label className="block text-[14px] mb-2">
                Selfie Holding ID <span className="text-[#6B6B6B]">(Optional)</span>
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  capture="user"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload('selfie', file);
                    e.target.value = '';
                  }}
                  className="hidden"
                  id="selfie-upload"
                />
                <label
                  htmlFor="selfie-upload"
                  className="w-full h-32 rounded-[12px] bg-gray-50 border-2 border-dashed border-gray-300 hover:border-[#E0001B] transition-colors flex flex-col items-center justify-center cursor-pointer overflow-hidden"
                >
                  {selfieWithId ? (
                    <img src={selfieWithId} alt="Selfie with ID" className="w-full h-full object-cover" />
                  ) : (
                    <>
                      <Camera className="w-8 h-8 text-[#6B6B6B] mb-2" />
                      <span className="text-[14px] text-[#6B6B6B] text-center px-4">Take a photo holding your ID next to your face</span>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Vehicle Registration — optional, own vehicle only */}
            {vehicleOwnership === 'own' && (fleetType === 'motorcycle' || fleetType === 'bicycle') && (
              <div>
                <label className="block text-[14px] mb-2">
                  Vehicle Registration <span className="text-[#6B6B6B]">(Optional)</span>
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload('vehicle', file);
                      e.target.value = '';
                    }}
                    className="hidden"
                    id="vehicle-upload"
                  />
                  <label
                    htmlFor="vehicle-upload"
                    className="w-full h-32 rounded-[12px] bg-gray-50 border-2 border-dashed border-gray-300 hover:border-[#E0001B] transition-colors flex flex-col items-center justify-center cursor-pointer overflow-hidden"
                  >
                    {vehicleReg ? (
                      <img src={vehicleReg} alt="Vehicle Reg" className="w-full h-full object-cover" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-[#6B6B6B] mb-2" />
                        <span className="text-[14px] text-[#6B6B6B]">Upload vehicle papers</span>
                      </>
                    )}
                  </label>
                </div>
              </div>
            )}

            {!documentsReady && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-[12px] text-[13px] text-yellow-800">
                <strong>To continue, complete:</strong>
                <ul className="mt-1 ml-4 list-disc">
                  {!idDocumentType && <li>Select your ID type</li>}
                  {!profilePhoto && <li>Upload a profile photo</li>}
                  {idDocumentType && !idCard && (
                    <li>Upload your {getIdDocumentLabel(idDocumentType)}</li>
                  )}
                </ul>
              </div>
            )}

            <button
              type="button"
              onClick={handleDocumentsSubmit}
              disabled={!documentsReady}
              className="w-full h-12 rounded-[12px] bg-[#E0001B] text-white hover:bg-[#C00017] transition-colors shadow-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Step 8: Availability & Terms */}
        {step === 'availability-terms' && (
          <div className="space-y-6">
            {/* Work Availability */}
            <div>
              <h3 className="text-[18px] font-medium mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#E0001B]" />
                Work Availability
              </h3>
              
              <div className="space-y-4">
                <label className="flex items-start gap-3 cursor-pointer group p-4 rounded-[12px] border-2 border-gray-200 hover:border-[#E0001B] transition-colors">
                  <input
                    type="checkbox"
                    checked={flexibleSchedule}
                    onChange={(e) => {
                      setFlexibleSchedule(e.target.checked);
                      if (e.target.checked) {
                        setAvailableDays([]);
                      }
                    }}
                    className="mt-0.5 w-5 h-5 rounded border-2 border-gray-300 text-[#E0001B] focus:ring-[#E0001B]"
                  />
                  <div>
                    <span className="text-[14px] font-medium text-[#111111] group-hover:text-[#E0001B] transition-colors">
                      Flexible schedule
                    </span>
                    <p className="text-[12px] text-[#6B6B6B] mt-0.5">
                      I&apos;ll choose my working days when I go online — no fixed schedule needed now
                    </p>
                  </div>
                </label>

                {!flexibleSchedule && (
                  <div>
                    <label className="block text-[14px] mb-2">
                      Available Days <span className="text-[#E0001B]">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => toggleDay(day)}
                          className={`h-12 rounded-[12px] border-2 transition-colors ${
                            availableDays.includes(day)
                              ? 'bg-[#E0001B] border-[#E0001B] text-white'
                              : 'bg-white border-gray-200 text-[#111111] hover:border-[#E0001B]'
                          }`}
                        >
                          {day.slice(0, 3)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="preferredHours" className="block text-[14px] mb-2">
                    Preferred Working Hours <span className="text-[#E0001B]">*</span>
                  </label>
                  <select
                    id="preferredHours"
                    value={preferredHours}
                    onChange={(e) => setPreferredHours(e.target.value)}
                    className="w-full h-14 px-4 rounded-[12px] bg-gray-50 border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
                    required
                  >
                    <option value="">Select preferred hours</option>
                    <option value="morning">Morning (6AM - 12PM)</option>
                    <option value="afternoon">Afternoon (12PM - 6PM)</option>
                    <option value="evening">Evening (6PM - 12AM)</option>
                    <option value="flexible">Flexible (Any time)</option>
                    <option value="fulltime">Full-time (All day)</option>
                    <option value="undecided">I&apos;ll decide when I start</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Terms & Agreements */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-[18px] font-medium mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#E0001B]" />
                Terms & Agreements
              </h3>
              
              <label className="flex items-start gap-3 cursor-pointer group p-4 rounded-[12px] bg-gray-50 border border-gray-200">
                <input
                  type="checkbox"
                  checked={agreementsAccepted}
                  onChange={(e) => setAgreementsAccepted(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-2 border-gray-300 text-[#E0001B] focus:ring-[#E0001B]"
                />
                <span className="text-[14px] text-[#111111] group-hover:text-[#E0001B] transition-colors">
                  I agree to GO7&apos;s{' '}
                  <a href="#" className="text-[#E0001B] underline">Terms of Service</a>,{' '}
                  <a href="#" className="text-[#E0001B] underline">Privacy Policy</a>, and consent to identity
                  verification and data processing needed to operate as a rider.
                </span>
              </label>

              <div className="mt-4 p-4 bg-gray-50 rounded-[12px]">
                <p className="text-[12px] text-[#6B6B6B]">
                  Your application may be reviewed within 24–48 hours. GO7 may contact you if additional
                  documents are needed.
                </p>
              </div>
            </div>

            {/* Debug: Show what's blocking submission */}
            {(!agreementsAccepted || (!flexibleSchedule && availableDays.length === 0) || !preferredHours) && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-[12px] text-[13px] text-yellow-800">
                <strong>Please complete:</strong>
                <ul className="mt-1 ml-4 list-disc">
                  {!agreementsAccepted && <li>Accept terms and agreements</li>}
                  {!flexibleSchedule && availableDays.length === 0 && <li>Select available days or choose flexible schedule</li>}
                  {!preferredHours && <li>Select preferred working hours</li>}
                </ul>
              </div>
            )}

            <button
              onClick={handleFinalSubmit}
              disabled={loading || !agreementsAccepted || (!flexibleSchedule && availableDays.length === 0) || !preferredHours}
              className="w-full h-14 rounded-[12px] bg-[#00B277] text-white hover:bg-[#009963] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium text-[16px]"
            >
              {loading ? (
                <>
                  <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-6 h-6" />
                  Complete Sign Up
                </>
              )}
            </button>

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-[12px] flex items-start gap-3">
                <div className="text-red-600 text-[14px]">{error}</div>
              </div>
            )}

            <p className="text-[12px] text-[#6B6B6B] text-center">
              Your application will be reviewed within 24-48 hours
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
